(() => {
  const normalize = (value) =>
    (value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const isProductPage = () =>
    document.body.classList.contains('template-product') ||
    document.documentElement.classList.contains('template-product') ||
    /^\/products(?:\/|$)/.test(location.pathname);

  const visible = (el) => {
    if (!(el instanceof HTMLElement)) return false;
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    const r = el.getBoundingClientRect();
    return r.width > 80 && r.height > 40;
  };

  const findHeading = () => {
    const headings = [...document.querySelectorAll('h1, h2, h3')]
      .filter(visible);

    return headings.find((el) => normalize(el.textContent) === 'how to apply') || null;
  };

  const findSection = (heading) => {
    if (!heading) return null;

    let node = heading;
    while (node && node !== document.body) {
      if (
        node.matches?.('section') &&
        node.getBoundingClientRect().width >= window.innerWidth * 0.75
      ) {
        return node;
      }
      node = node.parentElement;
    }

    return heading.closest('.shopify-section') || heading.parentElement;
  };

  const lowestCommonAncestor = (a, b, stop) => {
    if (!a || !b) return stop;

    const ancestors = new Set();
    let x = a;

    while (x) {
      ancestors.add(x);
      if (x === stop) break;
      x = x.parentElement;
    }

    let y = b;
    while (y) {
      if (ancestors.has(y)) return y;
      if (y === stop) break;
      y = y.parentElement;
    }

    return stop;
  };

  const largestMedia = (section, copyCandidate) => {
    const media = [...section.querySelectorAll('img, video')]
      .filter((el) => {
        if (!visible(el)) return false;
        if (copyCandidate && copyCandidate.contains(el)) return false;
        const r = el.getBoundingClientRect();
        return r.width > 250 && r.height > 250;
      })
      .sort((a, b) => {
        const ar = a.getBoundingClientRect();
        const br = b.getBoundingClientRect();
        return (br.width * br.height) - (ar.width * ar.height);
      });

    return media[0] || null;
  };

  const findCopyContainer = (heading, section) => {
    let node = heading.parentElement;
    let best = heading.parentElement;

    while (node && node !== section) {
      const r = node.getBoundingClientRect();

      if (
        r.width >= 320 &&
        r.height >= 280 &&
        r.width <= section.getBoundingClientRect().width * 0.62
      ) {
        best = node;
      }

      node = node.parentElement;
    }

    return best;
  };

  const directChildUnder = (node, ancestor) => {
    if (!node || !ancestor) return node;

    let current = node;
    let previous = node;

    while (current && current !== ancestor) {
      previous = current;
      current = current.parentElement;
    }

    return previous;
  };

  const tagSteps = (copy) => {
    const labels = [
      'start clean and dry.',
      'trace the upper lash line.',
      'allow to dry.'
    ];

    const tagged = new Set();

    labels.forEach((label) => {
      const textNode = [...copy.querySelectorAll('h3, h4, strong, p, div, span')]
        .filter(visible)
        .find((el) => normalize(el.textContent) === label);

      if (!textNode) return;

      let row = textNode;
      while (
        row.parentElement &&
        row.parentElement !== copy &&
        row.parentElement.getBoundingClientRect().width <= copy.getBoundingClientRect().width * 1.02
      ) {
        const parent = row.parentElement;
        const text = normalize(parent.textContent);

        if (
          text.length > label.length &&
          text.length < 240 &&
          parent.getBoundingClientRect().height < 150
        ) {
          row = parent;
        } else {
          break;
        }
      }

      row.classList.add('elizna-howto-fit-v1__step');
      tagged.add(row);
    });

    if (tagged.size) {
      const first = [...tagged][0];
      const parent = first.parentElement;
      if (parent && [...tagged].every((el) => el.parentElement === parent)) {
        parent.classList.add('elizna-howto-fit-v1__steps');
      }
    }

    const intro = [...copy.querySelectorAll('p')]
      .filter(visible)
      .find((el) =>
        normalize(el.textContent).includes(
          'a precise once-daily step designed to fit seamlessly into your routine'
        )
      );

    if (intro) intro.classList.add('elizna-howto-fit-v1__intro');

    const note = [...copy.querySelectorAll('p, small, div')]
      .filter(visible)
      .find((el) =>
        normalize(el.textContent).includes('use once daily') &&
        normalize(el.textContent).includes('external use only')
      );

    if (note) note.classList.add('elizna-howto-fit-v1__note');
  };

  const measureChrome = (section) => {
    const headerCandidates = [
      document.querySelector('.header-wrapper'),
      document.querySelector('header'),
      document.querySelector('[class*="header-wrapper"]')
    ].filter(Boolean);

    let headerHeight = 0;

    headerCandidates.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.height > headerHeight && r.height < 180) {
        headerHeight = r.height;
      }
    });

    const stickyCandidates = [
      document.querySelector('.elizna-sticky-purchase'),
      document.querySelector('[class*="sticky-purchase"]'),
      document.querySelector('[data-sticky-purchase]')
    ].filter(Boolean);

    let stickyHeight = 0;

    stickyCandidates.forEach((el) => {
      const style = getComputedStyle(el);
      const r = el.getBoundingClientRect();

      if (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        r.height > stickyHeight &&
        r.height < 160
      ) {
        stickyHeight = r.height;
      }
    });

    if (!headerHeight) headerHeight = 78;
    if (!stickyHeight) stickyHeight = 68;

    section.style.setProperty('--elizna-howto-header-h', `${Math.round(headerHeight)}px`);
    section.style.setProperty('--elizna-howto-sticky-h', `${Math.round(stickyHeight)}px`);
  };

  const apply = () => {
    if (!isProductPage() || window.innerWidth < 990) return;

    const heading = findHeading();
    if (!heading) return;

    const section = findSection(heading);
    if (!section) return;

    section.classList.add('elizna-howto-fit-v1');

    const copyCandidate = findCopyContainer(heading, section);
    const mediaElement = largestMedia(section, copyCandidate);

    if (!mediaElement) {
      measureChrome(section);
      return;
    }

    let grid = lowestCommonAncestor(copyCandidate, mediaElement, section);

    if (grid === section) {
      grid = section;
    }

    grid.classList.add('elizna-howto-fit-v1__grid');

    const copy = directChildUnder(copyCandidate, grid);
    const media = directChildUnder(mediaElement, grid);

    if (copy) copy.classList.add('elizna-howto-fit-v1__copy');
    if (media) media.classList.add('elizna-howto-fit-v1__media');

    if (copy) tagSteps(copy);

    measureChrome(section);
  };

  let raf = 0;
  const schedule = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      apply();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  window.addEventListener('load', schedule, { once: true });
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', schedule);
})();
